import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  Text,
  Tooltip,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { ChartColorDot } from './ChartColorDot'
import { countFittingItems } from './countFittingItems'
import { useChartSeries } from './ChartSeriesContext'
import type { ChartSeries } from './types'

import sharedStyles from './chart-shared.module.css'
import styles from './ChartLegendView.module.css'

// Small horizontal reserve so entries never crowd the legend's edge.
const MIN_LEFT_GAP_PX = 24
// Width reserved for the "+N more" label when deciding how many entries fit.
const MORE_LABEL_RESERVE_PX = 72

function LegendItem({ series }: { series: ChartSeries }): JSX.Element {
  return (
    <span className={sharedStyles.legendItem}>
      <ChartColorDot
        color={series.color}
        dashed={series.kind === 'comparison'}
      />
      {series.icon != null && (
        <span className={sharedStyles.legendItemIcon} aria-hidden="true">
          {series.icon}
        </span>
      )}
      <Text size="sm" aria-hidden="true">
        {series.label}
      </Text>
      <VisuallyHidden>{series.label}</VisuallyHidden>
    </span>
  )
}

interface ChartLegendViewProps {
  /** Accessible name for the legend region. */
  ariaLabel?: string
}

/**
 * The chart legend. Context-driven: renders one entry (colour swatch + icon +
 * label) per primary series from {@link ChartSeriesContext}, so its colours match
 * the line exactly. When entries don't all fit it collapses the overflow into a
 * "+N more" control whose tooltip lists the rest. Colours/icons/labels come from
 * the registry — nothing is passed in.
 */
export function ChartLegendView({
  ariaLabel = 'Chart legend',
}: ChartLegendViewProps): JSX.Element | null {
  const allSeries = useChartSeries()
  // Memoized so the layout effect below doesn't tear down and recreate the
  // ResizeObserver on every render — only when the underlying series change.
  const series = useMemo(
    () => allSeries.filter((s) => s.kind === 'primary'),
    [allSeries],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const ghostRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(series.length)

  useLayoutEffect(() => {
    const container = containerRef.current
    const ghost = ghostRef.current
    if (!container || !ghost) return undefined

    const measure = (): void => {
      const rightEdges: number[] = []
      for (const child of Array.from(ghost.children)) {
        if (child instanceof HTMLElement) {
          rightEdges.push(child.offsetLeft + child.offsetWidth)
        }
      }
      setVisibleCount(
        countFittingItems(
          rightEdges,
          container.clientWidth - MIN_LEFT_GAP_PX,
          MORE_LABEL_RESERVE_PX,
        ),
      )
    }

    measure()
    // Watch both the container (available width) and the ghost row (its natural
    // width grows once web fonts / icons finish loading after first paint).
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    observer.observe(ghost)
    return (): void => observer.disconnect()
  }, [series])

  if (series.length === 0) return null

  const visible = series.slice(0, visibleCount)
  const hidden = series.slice(visibleCount)

  return (
    <div
      ref={containerRef}
      className={styles.legend}
      // A named group describes the chart series without adding a page landmark.
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="group"
      aria-label={ariaLabel}
    >
      {/* Off-screen full set, used only to measure how many entries fit. */}
      <div ref={ghostRef} className={styles.ghost} aria-hidden="true">
        {series.map((s) => (
          <LegendItem key={s.id} series={s} />
        ))}
      </div>

      {visible.map((s) => (
        <LegendItem key={s.id} series={s} />
      ))}

      {hidden.length > 0 && (
        <Tooltip
          content={
            <div className={styles.moreList}>
              {hidden.map((s) => (
                <LegendItem key={s.id} series={s} />
              ))}
            </div>
          }
        >
          <UnstyledButton
            type="button"
            className={styles.moreLabel}
            aria-label={`${hidden.length} more: ${hidden
              .map((s) => s.label)
              .join(', ')}`}
          >
            +{hidden.length} more
          </UnstyledButton>
        </Tooltip>
      )}
    </div>
  )
}
